import { gql } from '@apollo/client';

// Basic products query for the homepage and catalog - with language support
export const GET_PRODUCTS = gql`
  query getProducts(
    $first: Int!
    $language: LanguageCode
    $country: CountryCode
  ) @inContext(language: $language, country: $country) {
    products(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          tags
          productType
          vendor
          metafields(identifiers: [
            {namespace: "product", key: "short_description"},
            {namespace: "nutrition", key: "rating"},
            {namespace: "product", key: "review_count"},
            {namespace: "properties", key: "country_origin"}
          ]) {
            namespace
            key
            value
          }
        }
      }
    }
  }
`;

// Single product query for product pages - with language support
export const GET_PRODUCT = gql`
  query getProduct(
    $handle: String!
    $language: LanguageCode
    $country: CountryCode
  ) @inContext(language: $language, country: $country) {
    product(handle: $handle) {
      id
      title
      handle
      description
      descriptionHtml
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
            width
            height
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            availableForSale
            quantityAvailable
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            selectedOptions {
              name
              value
            }
            image {
              url
              altText
            }
          }
        }
      }
      tags
      productType
      vendor
      options {
        id
        name
        values
      }
      metafields(identifiers: [
        {namespace: "product", key: "short_description"},
        {namespace: "product", key: "ingredients"},
        {namespace: "product", key: "usage_tips"},
        {namespace: "allergens", key: "contains"},
        {namespace: "nutrition", key: "rating"},
        {namespace: "nutrition", key: "energy_kcal"},
        {namespace: "nutrition", key: "protein_g"},
        {namespace: "nutrition", key: "fat_total_g"},
        {namespace: "nutrition", key: "carbs_total_g"},
        {namespace: "nutrition", key: "carbs_fiber_g"},
        {namespace: "nutrition", key: "sodium_mg"},
        {namespace: "product", key: "review_count"},
        {namespace: "properties", key: "country_origin"},
        {namespace: "properties", key: "organic"},
        {namespace: "health", key: "active_compounds"},
        {namespace: "health", key: "health_benefits"}
      ]) {
        namespace
        key
        value
        type
      }
    }
  }
`;

// Shop information query
export const GET_SHOP_INFO = gql`
  query getShopInfo {
    shop {
      name
      description
      primaryDomain {
        url
      }
      paymentSettings {
        currencyCode
        enabledPresentmentCurrencies
      }
    }
  }
`;

// Products by collection query - with language support
export const GET_PRODUCTS_BY_COLLECTION = gql`
  query getProductsByCollection(
    $handle: String!
    $first: Int!
    $language: LanguageCode
  ) @inContext(language: $language) {
    collection(handle: $handle) {
      id
      title
      description
      products(first: $first) {
        edges {
          node {
            id
            title
            handle
            description
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 3) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 5) {
              edges {
                node {
                  id
                  availableForSale
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Search products query
export const SEARCH_PRODUCTS = gql`
  query searchProducts($query: String!, $first: Int!, $language: LanguageCode, $country: CountryCode)
  @inContext(language: $language, country: $country) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          handle
          description
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 3) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 3) {
            edges {
              node {
                id
                availableForSale
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
          tags
          productType
          vendor
          metafields(identifiers: [
            {namespace: "product", key: "short_description"},
            {namespace: "nutrition", key: "rating"},
            {namespace: "product", key: "review_count"},
            {namespace: "properties", key: "country_origin"}
          ]) {
            namespace
            key
            value
          }
        }
      }
    }
  }
`;

// Search recipes/articles query
export const SEARCH_RECIPES = gql`
  query searchRecipes($query: String!, $first: Int!) {
    blogs(first: 5) {
      edges {
        node {
          id
          title
          handle
          articles(first: $first, query: $query) {
            edges {
              node {
                id
                title
                handle
                excerpt
                content
                contentHtml
                image {
                  url
                  altText
                }
                publishedAt
                author: authorV2 {
                  firstName
                  lastName
                }
                tags
                metafields(identifiers: [
                  {namespace: "custom", key: "ingredients"},
                  {namespace: "custom", key: "instructions"},
                  {namespace: "custom", key: "prep_time"},
                  {namespace: "custom", key: "difficulty"},
                  {namespace: "custom", key: "portions"},
                  {namespace: "custom", key: "calories"},
                  {namespace: "custom", key: "protein"},
                  {namespace: "custom", key: "fat"},
                  {namespace: "custom", key: "carbs"},
                  {namespace: "custom", key: "rating_average"},
                  {namespace: "custom", key: "rating_count"},
                  {namespace: "custom", key: "tags"},
                  {namespace: "custom", key: "description"},
                  {namespace: "custom", key: "image_url"},
                  {namespace: "custom", key: "processed"}
                ]) {
                  namespace
                  key
                  value
                  type
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Simple blogs query first - to debug access
export const GET_BLOGS = gql`
  query getBlogs($language: LanguageCode) @inContext(language: $language) {
    blogs(first: 10) {
      edges {
        node {
          id
          title
          handle
        }
      }
    }
  }
`;

// Recipes/Articles query from Shopify blog
export const GET_RECIPES = gql`
  query getRecipes($first: Int!, $language: LanguageCode)
  @inContext(language: $language) {
    blogs(first: 5) {
      edges {
        node {
          id
          title
          handle
          articles(first: $first) {
            edges {
              node {
                id
                title
                handle
                excerpt
                content
                contentHtml
                image {
                  url
                  altText
                }
                publishedAt
                author: authorV2 {
                  firstName
                  lastName
                }
                tags
                metafields(identifiers: [
                  {namespace: "custom", key: "ingredients"},
                  {namespace: "custom", key: "instructions"},
                  {namespace: "custom", key: "prep_time"},
                  {namespace: "custom", key: "difficulty"},
                  {namespace: "custom", key: "portions"},
                  {namespace: "custom", key: "calories"},
                  {namespace: "custom", key: "protein"},
                  {namespace: "custom", key: "fat"},
                  {namespace: "custom", key: "carbs"},
                  {namespace: "custom", key: "rating_average"},
                  {namespace: "custom", key: "rating_count"},
                  {namespace: "custom", key: "tags"},
                  {namespace: "custom", key: "description"},
                  {namespace: "custom", key: "image_url"},
                  {namespace: "custom", key: "processed"}
                ]) {
                  namespace
                  key
                  value
                  type
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Single recipe query
export const GET_RECIPE = gql`
  query getRecipe($handle: String!, $language: LanguageCode)
  @inContext(language: $language) {
    blogs(first: 10) {
      edges {
        node {
          articleByHandle(handle: $handle) {
            id
            title
            handle
            excerpt
            content
            contentHtml
            image {
              url
              altText
            }
            publishedAt
            author: authorV2 {
              firstName
              lastName
            }
            tags
            metafields(identifiers: [
              {namespace: "recipe", key: "ingredients"},
              {namespace: "recipe", key: "prep_time"},
              {namespace: "recipe", key: "cook_time"},
              {namespace: "recipe", key: "total_time"},
              {namespace: "recipe", key: "servings"},
              {namespace: "recipe", key: "difficulty"},
              {namespace: "recipe", key: "cuisine"},
              {namespace: "recipe", key: "rating"},
              {namespace: "recipe", key: "review_count"},
              {namespace: "recipe", key: "calories"},
              {namespace: "recipe", key: "instructions"},
              {namespace: "recipe", key: "nutrition"},
              {namespace: "recipe", key: "video_url"},
              {namespace: "recipe", key: "meal_type"},
              {namespace: "recipe", key: "dietary_restrictions"},
              {namespace: "custom", key: "featured"},
              {namespace: "custom", key: "is_new"}
            ]) {
              namespace
              key
              value
              type
            }
          }
        }
      }
    }
  }
`;

// Paginated products query for loading ALL products (500+)
export const GET_PRODUCTS_PAGINATED = gql`
  query getProductsPaginated(
    $first: Int!
    $after: String
    $language: LanguageCode
    $country: CountryCode
  ) @inContext(language: $language, country: $country) {
    products(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          title
          handle
          description
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          tags
          productType
          vendor
          metafields(identifiers: [
            {namespace: "product", key: "short_description"},
            {namespace: "nutrition", key: "rating"},
            {namespace: "product", key: "review_count"},
            {namespace: "properties", key: "country_origin"}
          ]) {
            namespace
            key
            value
          }
        }
      }
    }
  }
`;
// Query for getting all collections
export const GET_COLLECTIONS = gql`
  query getCollections($first: Int!, $language: LanguageCode)
  @inContext(language: $language) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          image {
            url
            altText
          }
          products(first: 10) {
            edges {
              node {
                id
                title
                handle
              }
            }
          }
        }
      }
    }
  }
`;

// Query for getting products from a specific collection
export const GET_COLLECTION_PRODUCTS = gql`
  query getCollectionProducts(
    $handle: String!
    $first: Int!
    $after: String
    $language: LanguageCode
  ) @inContext(language: $language) {
    collectionByHandle(handle: $handle) {
      id
      title
      description
      image {
        url
        altText
      }
      products(first: $first, after: $after) {
        edges {
          node {
            id
            title
            handle
            description
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 5) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  quantityAvailable
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            tags
            metafields(identifiers: [
              {namespace: "product", key: "short_description"},
              {namespace: "nutrition", key: "rating"},
              {namespace: "product", key: "review_count"},
              {namespace: "properties", key: "country_origin"},
              {namespace: "product", key: "ingredients"},
              {namespace: "product", key: "usage_tips"},
              {namespace: "allergens", key: "contains"},
              {namespace: "nutrition", key: "energy_kcal"},
              {namespace: "nutrition", key: "protein_g"},
              {namespace: "nutrition", key: "fat_total_g"},
              {namespace: "nutrition", key: "carbs_total_g"},
              {namespace: "nutrition", key: "carbs_fiber_g"},
              {namespace: "nutrition", key: "sodium_mg"},
              {namespace: "health", key: "active_compounds"},
              {namespace: "health", key: "health_benefits"},
              {namespace: "properties", key: "organic"}
            ]) {
              namespace
              key
              value
              type
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;
